import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WeakAreaIdentification = ({ weakAreas, onPracticeClick }) => {
  const getSeverityColor = (severity) => {
    if (severity === 'critical') return 'bg-error text-error';
    if (severity === 'moderate') return 'bg-warning text-warning';
    return 'bg-accent text-accent';
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'critical') return 'AlertTriangle';
    if (severity === 'moderate') return 'AlertCircle';
    return 'Info';
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-elevation-2 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            Weak Area Identification
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Focus areas for improvement
          </p>
        </div>
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-warning/20 flex items-center justify-center">
          <Icon name="Target" size={24} className="text-warning" />
        </div>
      </div>
      {weakAreas?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle2" size={32} className="text-success" />
          </div>
          <h4 className="text-lg md:text-xl font-semibold text-foreground mb-2">
            Excellent Performance!
          </h4>
          <p className="text-sm md:text-base text-muted-foreground">
            No weak areas identified. Keep up the great work!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {weakAreas?.map((area, index) => (
            <div
              key={index}
              className="bg-muted/30 rounded-lg p-4 md:p-6 border border-border transition-smooth hover:shadow-elevation-1"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-8 h-8 rounded-lg ${getSeverityColor(area?.severity)}/20 flex items-center justify-center flex-shrink-0 mt-1`}>
                      <Icon 
                        name={getSeverityIcon(area?.severity)} 
                        size={18} 
                        className={getSeverityColor(area?.severity)?.split(' ')?.[1]}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-base md:text-lg font-semibold text-foreground">
                          {area?.knowledgeArea}
                        </h4>
                        <span className={`px-2 py-1 ${getSeverityColor(area?.severity)}/20 ${getSeverityColor(area?.severity)?.split(' ')?.[1]} text-xs font-medium rounded-full capitalize`}>
                          {area?.severity}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {area?.description}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-muted-foreground mb-3">
                        <div className="flex items-center gap-2">
                          <Icon name="Target" size={16} />
                          <span>Current: {area?.currentScore}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="TrendingUp" size={16} />
                          <span>Target: {area?.targetScore}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="BookOpen" size={16} />
                          <span>{area?.recommendedQuestions} questions recommended</span>
                        </div>
                      </div>

                      {area?.specificTopics && area?.specificTopics?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Specific topics to review:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {area?.specificTopics?.map((topic, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-muted text-foreground text-xs rounded-full border border-border"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 lg:min-w-[140px]">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Play"
                    iconPosition="left"
                    onClick={() => onPracticeClick(area)}
                    fullWidth
                  >
                    Practice Now
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="BookOpen"
                    iconPosition="left"
                    fullWidth
                  >
                    Study Guide
                  </Button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">
                    Progress to target
                  </span>
                  <span className="text-xs font-semibold text-foreground data-text">
                    {Math.round((area?.currentScore / area?.targetScore) * 100)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getSeverityColor(area?.severity)?.split(' ')?.[1]} bg-current transition-all duration-500`}
                    style={{ width: `${(area?.currentScore / area?.targetScore) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WeakAreaIdentification;